import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { PeriodoValor } from '@/shared/types/filtroPeriodo';

import type {
  AbaDaLista,
  AtalhoDeSituacao,
  CategoriaDoChip,
  EstadoDaExportacao,
  FiltrosDeAssinaturas,
  LinhaSelecionavel,
  NegociacaoAssinaturasState,
} from '../types/negociacaoAssinaturasTypes';
import {
  FILTROS_VAZIOS,
  LIMITE_DE_SELECAO,
  situacaoDoAtalho,
  consultaDoDetalhe,
} from '../utils/consultaAssinaturas';
import { PERIODO_DAS_ASSINATURAS_VAZIO } from '../utils/periodoDasAssinaturas';
import { VALOR_NAO_INFORMADA, VALOR_NOME_PROPRIO } from '../utils/rotulosAssinatura';
import {
  adicionarCasosDeParticipacoes,
  invalidarConsultasDeParticipacoes,
  limparParticipacoesDoContexto,
} from './participacoesReducers';
import {
  consultarAssinaturas,
  consultarAssinaturasDaNegociacao,
  consultarOpcoesDeFiltro,
  exportarAssinaturas,
} from './actions';

const initialState: NegociacaoAssinaturasState = {
  escritorio: null,
  buscaDigitada: '',
  buscaAplicada: '',
  filtros: FILTROS_VAZIOS,
  periodo: PERIODO_DAS_ASSINATURAS_VAZIO,
  aba: 'ALL',
  pagina: 1,
  selecionados: {},
  resultado: null,
  carregando: false,
  erro: null,
  consultaVigente: null,
  exportacoes: {},
  detalhes: {},
  opcoesDeFiltro: { opcoes: null, carregando: false, erro: null, consultaVigente: null, busca: '' },
  participacoes: {},
};

/** "Em nome próprio" e "Não informada" são do web e valem em qualquer escritório. */
const REPRESENTACOES_DE_QUALQUER_ESCRITORIO: readonly string[] = [
  VALOR_NOME_PROPRIO,
  VALOR_NAO_INFORMADA,
];

const EXPORTACAO_INICIAL: EstadoDaExportacao = {
  exportando: false,
  erro: null,
  ultimoExport: null,
  ultimoCorpo: null,
};

/**
 * Toda mudança da consulta (busca aplicada, filtros, período, sub-aba ou atalho) volta à página 1 e
 * limpa a seleção: uma negociação marcada sob outro critério não pode ir para o arquivo de uma
 * consulta que não a mostra. A troca de página, sozinha, mantém a seleção.
 */
function consultaMudou(state: NegociacaoAssinaturasState): void {
  state.pagina = 1;
  state.selecionados = {};
}

function marcar(state: NegociacaoAssinaturasState, linha: LinhaSelecionavel): void {
  if (linha.casoId in state.selecionados) return;
  if (Object.keys(state.selecionados).length >= LIMITE_DE_SELECAO) return;
  state.selecionados[linha.casoId] = linha.pessoas;
}

const negociacaoAssinaturasSlice = createSlice({
  name: 'negociacaoAssinaturas',
  initialState,
  reducers: {
    buscaDigitada: (state, action: PayloadAction<string>) => {
      state.buscaDigitada = action.payload;
    },
    buscaAplicada: (state, action: PayloadAction<string>) => {
      const busca = action.payload.trim();
      if (busca === state.buscaAplicada) return;
      state.buscaAplicada = busca;
      consultaMudou(state);
    },
    filtrosAplicados: (state, action: PayloadAction<FiltrosDeAssinaturas>) => {
      state.filtros = action.payload;
      consultaMudou(state);
    },
    filtroRemovido: (
      state,
      action: PayloadAction<{ categoria: CategoriaDoChip; valor: string }>,
    ) => {
      const { categoria, valor } = action.payload;
      if (categoria === 'periodo') {
        state.periodo = PERIODO_DAS_ASSINATURAS_VAZIO;
      } else {
        const valores: string[] = state.filtros[categoria];
        state.filtros = {
          ...state.filtros,
          [categoria]: valores.filter((atual) => atual !== valor),
        };
      }
      consultaMudou(state);
    },
    atalhoEscolhido: (state, action: PayloadAction<AtalhoDeSituacao>) => {
      state.filtros.situacaoIn = situacaoDoAtalho(action.payload);
      consultaMudou(state);
    },
    periodoAplicado: (state, action: PayloadAction<PeriodoValor>) => {
      state.periodo = action.payload;
      consultaMudou(state);
    },
    filtrosLimpos: (state) => {
      state.filtros = FILTROS_VAZIOS;
      state.periodo = PERIODO_DAS_ASSINATURAS_VAZIO;
      consultaMudou(state);
    },
    buscaEFiltrosLimpos: (state) => {
      state.buscaDigitada = '';
      state.buscaAplicada = '';
      state.filtros = FILTROS_VAZIOS;
      state.periodo = PERIODO_DAS_ASSINATURAS_VAZIO;
      consultaMudou(state);
    },
    // Fora de "Todos" a consulta não leva o Status (`filtrosDaListaParaApi`): mantê-lo deixaria um
    // chip e um resumo de export com um critério que o core não recebe. A Lista faz o mesmo
    // (`useNegociacoesPage`).
    abaDefinida: (state, action: PayloadAction<AbaDaLista>) => {
      if (action.payload === state.aba) return;
      state.aba = action.payload;
      if (action.payload !== 'ALL' && state.filtros.status.length > 0) {
        state.filtros = { ...state.filtros, status: [] };
      }
      consultaMudou(state);
    },
    paginaEscolhida: (state, action: PayloadAction<number>) => {
      state.pagina = Math.max(1, action.payload);
    },
    selecaoAlternada: (state, action: PayloadAction<LinhaSelecionavel>) => {
      if (action.payload.casoId in state.selecionados) {
        delete state.selecionados[action.payload.casoId];
        return;
      }
      marcar(state, action.payload);
    },
    paginaSelecionada: (state, action: PayloadAction<LinhaSelecionavel[]>) => {
      action.payload.forEach((linha) => marcar(state, linha));
    },
    paginaDesmarcada: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((casoId) => {
        delete state.selecionados[casoId];
      });
    },
    selecaoLimpa: (state) => {
      state.selecionados = {};
    },
    exportacaoDescartada: (state, action: PayloadAction<string>) => {
      delete state.exportacoes[action.payload];
    },
    /**
     * O seletor de escritório não recarrega a página (review COR-11): o que veio do escritório
     * anterior sai de uma vez. Papel e nome de entidade escolhidos lá não existem aqui (o chip
     * mentiria e a lista viria vazia); a seleção e o último export são negociações e arquivo de lá;
     * o resultado traz a permissão de lá; as opções do Mais filtros são de lá. Busca, período,
     * atalho e os filtros genéricos continuam. O primeiro escritório só fica registrado: a consulta
     * que voltou do detalhe não pode perder nada.
     *
     * `consultaVigente` nulo descarta a resposta que ainda estava em voo no escritório anterior.
     */
    escritorioDefinido: (state, action: PayloadAction<string | null>) => {
      const anterior = state.escritorio;
      if (anterior === action.payload) return;
      state.escritorio = action.payload;
      if (anterior === null) return;
      state.filtros = {
        ...state.filtros,
        rotuloIn: [],
        representacaoIn: state.filtros.representacaoIn.filter((valor) =>
          REPRESENTACOES_DE_QUALQUER_ESCRITORIO.includes(valor),
        ),
      };
      consultaMudou(state);
      state.resultado = null;
      state.erro = null;
      state.carregando = true;
      state.consultaVigente = null;
      state.exportacoes = {};
      state.detalhes = {};
      state.participacoes = {};
      state.opcoesDeFiltro = initialState.opcoesDeFiltro;
    },
  },
  extraReducers: (builder) => {
    adicionarCasosDeParticipacoes(builder);
    builder
      .addCase(consultarAssinaturas.pending, (state, action) => {
        invalidarConsultasDeParticipacoes(state, { escopo: 'lista' });
        state.consultaVigente = action.meta.requestId;
        state.carregando = true;
        state.erro = null;
      })
      .addCase(consultarAssinaturas.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.consultaVigente) return;
        limparParticipacoesDoContexto(state, { escopo: 'lista' });
        state.resultado = action.payload;
        state.carregando = false;
      })
      .addCase(consultarAssinaturas.rejected, (state, action) => {
        if (action.meta.requestId !== state.consultaVigente) return;
        state.carregando = false;
        state.erro = action.meta.aborted
          ? null
          : (action.payload ?? {
              status: 0,
              mensagem: 'Não foi possível carregar as assinaturas dos acordos',
            });
      })
      .addCase(consultarAssinaturasDaNegociacao.pending, (state, action) => {
        invalidarConsultasDeParticipacoes(state, consultaDoDetalhe(action.meta.arg));
        const atual = state.detalhes[action.meta.arg];
        state.detalhes[action.meta.arg] = {
          resultado: atual?.resultado ?? null,
          carregando: true,
          erro: null,
          consultaVigente: action.meta.requestId,
        };
      })
      // A revalidação ao voltar o foco pode cruzar com uma consulta ainda em voo do mesmo caso:
      // só a última pode trocar o que o detalhe mostra.
      .addCase(consultarAssinaturasDaNegociacao.fulfilled, (state, action) => {
        const atual = state.detalhes[action.meta.arg];
        if (!atual || atual.consultaVigente !== action.meta.requestId) return;
        limparParticipacoesDoContexto(state, consultaDoDetalhe(action.meta.arg));
        state.detalhes[action.meta.arg] = {
          ...atual,
          resultado: action.payload,
          carregando: false,
          erro: null,
        };
      })
      .addCase(consultarAssinaturasDaNegociacao.rejected, (state, action) => {
        const atual = state.detalhes[action.meta.arg];
        if (!atual || atual.consultaVigente !== action.meta.requestId) return;
        state.detalhes[action.meta.arg] = {
          ...atual,
          carregando: false,
          erro: action.meta.aborted
            ? null
            : (action.payload ?? {
                status: 0,
                mensagem: 'Não foi possível carregar as assinaturas dos acordos',
              }),
        };
      })
      .addCase(exportarAssinaturas.pending, (state, action) => {
        state.exportacoes[action.meta.arg.chave] = {
          ...EXPORTACAO_INICIAL,
          exportando: true,
          ultimoCorpo: action.meta.arg.corpo,
          consultaVigente: action.meta.requestId,
        };
      })
      // Sem a entrada, a troca de escritório limpou o export enquanto ele corria: o desfecho é do
      // escritório anterior e não volta à tela.
      .addCase(exportarAssinaturas.fulfilled, (state, action) => {
        const atual = state.exportacoes[action.meta.arg.chave];
        if (!atual || atual.consultaVigente !== action.meta.requestId) return;
        state.exportacoes[action.meta.arg.chave] = {
          ...atual,
          exportando: false,
          ultimoExport: action.payload,
        };
      })
      .addCase(exportarAssinaturas.rejected, (state, action) => {
        const atual = state.exportacoes[action.meta.arg.chave];
        if (!atual || atual.consultaVigente !== action.meta.requestId) return;
        state.exportacoes[action.meta.arg.chave] = {
          ...atual,
          exportando: false,
          erro: action.meta.aborted
            ? null
            : (action.payload?.mensagem ?? 'Erro ao exportar assinaturas'),
        };
      })
      .addCase(consultarOpcoesDeFiltro.pending, (state, action) => {
        state.opcoesDeFiltro.consultaVigente = action.meta.requestId;
        state.opcoesDeFiltro.carregando = true;
        state.opcoesDeFiltro.erro = null;
      })
      // Cada letra digitada vira uma consulta: só a última pode trocar a página de opções.
      .addCase(consultarOpcoesDeFiltro.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.opcoesDeFiltro.consultaVigente) return;
        const pedido = action.meta.arg;
        const page = typeof pedido === 'string' ? 1 : (pedido.page ?? 1);
        const busca = typeof pedido === 'string' ? pedido : pedido.busca;
        const anterior = state.opcoesDeFiltro.opcoes;
        state.opcoesDeFiltro.opcoes =
          page === 1 || !anterior || state.opcoesDeFiltro.busca !== busca
            ? action.payload
            : {
                papeis: {
                  ...action.payload.papeis,
                  items: [...new Set([...anterior.papeis.items, ...action.payload.papeis.items])],
                },
                representacoes: {
                  ...action.payload.representacoes,
                  items: [
                    ...new Set([
                      ...anterior.representacoes.items,
                      ...action.payload.representacoes.items,
                    ]),
                  ],
                },
              };
        state.opcoesDeFiltro.busca = busca;
        state.opcoesDeFiltro.carregando = false;
      })
      .addCase(consultarOpcoesDeFiltro.rejected, (state, action) => {
        if (action.meta.requestId !== state.opcoesDeFiltro.consultaVigente) return;
        state.opcoesDeFiltro.carregando = false;
        state.opcoesDeFiltro.erro = action.meta.aborted
          ? null
          : (action.payload ?? 'Não foi possível carregar as opções de filtro');
      });
  },
});

export const {
  abaDefinida,
  atalhoEscolhido,
  buscaAplicada,
  buscaDigitada,
  buscaEFiltrosLimpos,
  escritorioDefinido,
  exportacaoDescartada,
  filtroRemovido,
  filtrosAplicados,
  filtrosLimpos,
  paginaDesmarcada,
  paginaEscolhida,
  paginaSelecionada,
  periodoAplicado,
  selecaoAlternada,
  selecaoLimpa,
} = negociacaoAssinaturasSlice.actions;

export const negociacaoAssinaturasReducer = negociacaoAssinaturasSlice.reducer;
